// StatisticsV2.tsx — page « Statistiques » du menu : fréquentation des lives et des vidéos
//
// Refaite le 2026-10-17 d'après la maquette validée par l'utilisateur
// (https://claude.ai/artifact/4dvQBVecTSFMbqh4yTcoYn), dans le langage du Dashboard.
// Liste : chiffres du haut, recherche et tri, diffusions (ou vidéos) rangées par jour
// avec une jauge d'audience par ligne. Détail : fil d'Ariane, chiffres de la diffusion
// (uniques, présents, pic, durée), grand graphe, puis carte et villes côte à côte.
// Tout se calcule dans le navigateur à partir des mêmes appels qu'avant ; le relevé
// chaque minute, le pas, les cumuls, le zoom et les liens graphe ↔ carte sont gardés.

import React, { useEffect, useMemo, useRef, useState } from 'react';

import CartesianGraph2   from '../../widget/dataDisplay/charts/CartesianGraph2';
import OpenStreetMapV2   from '../../widget/dataDisplay/maps/OpenStreetMapV2';
import ExtendablePanel   from '../../widget/windows/ExtendablePanel';

import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState        } from '../../../store/generalReducer';

import 'flag-icons/css/flag-icons.min.css';
import styles from './StatisticsV2.module.scss';

import { Coordinate } from '../../../dataTypes/geolocationManagment/types';

import {
  fetchLiveStats      , fetchVideoStats     ,
  fetchLiveHistogram  , fetchVideoHistogram , fetchViewersDetail,
  LiveStatRow         , VideoStatRow        , Histogram, ViewerLocation, ViewerDetail,
} from '../../../backendServices/statisticsV2Managment/api';

type Onglet = 'live' | 'video';
type Tri    = 'date' | 'audience';

interface Row {
  id       : number;
  name     : string;
  startDate: string;
  endDate  : string | null;
  active   : boolean;
  viewers  : number;
}

/** Relevé automatique : les chiffres d'une diffusion en cours bougent à chaque minute. */
const RAFRAICHISSEMENT_MS = 60_000;

/** Les pas proposés, du plus fin au plus large (libellés repris des anciennes stats). */
const PAS: { label: string; seconds: number }[] = [
  { label: '1 minute'  , seconds:     60 },
  { label: '5 minutes' , seconds:    300 },
  { label: '10 minutes', seconds:    600 },
  { label: '30 minutes', seconds:   1800 },
  { label: '1 heure'   , seconds:   3600 },
  { label: '6 heures'  , seconds:  21600 },
  { label: '12 heures' , seconds:  43200 },
  { label: '1 jour'    , seconds:  86400 },
  { label: '1 semaine' , seconds: 604800 },
];

/** Le pas proposé le plus proche de celui que l'API a retenu (elle peut le relever, ou choisir seule). */
const labelDuPas = (seconds: number): string =>
  PAS.reduce((best, p) => Math.abs(p.seconds - seconds) < Math.abs(best.seconds - seconds) ? p : best).label;

const COULEUR_HLS    = '#ff3b3b';
const COULEUR_XTREAM = '#3b82f6';
const COULEUR_CUMUL  = '#f59e0b';

/**
 * Distinction HLS / Xtream Code (barres en deux parties, deux couleurs sur la carte,
 * légende, détail dans la bulle). Masquée tant que seul Xtream Code est utilisé : l'API
 * fournit toujours le découpage, il suffit de passer ceci à `true` pour le réafficher.
 */
const DISTINGUER_MODES = false;

const CLE_CUMULS = 'statisticsV2.afficherCumuls';
const JOUR_MS    = 86_400_000;

// ── Textes ───────────────────────────────────────────────────────────────────

const heure = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';

const dateHeure = (iso: string): string => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

const jourDe = (iso: string | null): string => {
  if (!iso) return 'Date inconnue';
  const today  = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(iso); target.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - target.getTime()) / JOUR_MS);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Hier';
  const label = target.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: target.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** « 9:12 », « 18:10 » à l'intérieur d'une journée, sinon la date courte. */
const momentDe = (iso: string | null): string => {
  if (!iso) return '—';
  const j = jourDe(iso);
  return j === "Aujourd'hui" ? heure(iso) : `${j === 'Hier' ? 'hier' : new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} ${heure(iso)}`;
};

const duree = (ms: number): string => {
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m >= 48 * 60) return `${Math.round(m / 1440)} jours`;
  return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}` : `${m} min`;
};

/** La fin d'une diffusion : l'heure seule si elle finit le jour où elle a commencé. */
const finDe = (r: { startDate: string; endDate: string | null }): string =>
  r.endDate && new Date(r.endDate).toDateString() === new Date(r.startDate).toDateString() ? heure(r.endDate) : momentDe(r.endDate);

const formatTranche = (iso: string, seconds: number): string => {
  const d = new Date(iso);
  return seconds >= 86400 ? d.toLocaleDateString('fr-FR') : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const nombre = (n: number) => n.toLocaleString('fr-FR');
const pluriel = (n: number, un: string, plusieurs = `${un}s`) => `${nombre(n)} ${n > 1 ? plusieurs : un}`;

const TEINTES = [ '#1E3A8A', '#7C2D12', '#134E4A', '#4C1D95', '#831843', '#14532D', '#713F12', '#164E63' ];
const teinte  = (s: string) => TEINTES[Array.from(s).reduce((a, c) => a + c.charCodeAt(0), 0) % TEINTES.length];
const initiales = (s: string) => s.split(/[\s—–-]+/).filter(w => /^[A-Za-zÀ-ÖØ-öø-ÿ0-9]/.test(w)).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?';

/** Le jour de référence d'une ligne : début de la diffusion, ou dernière lecture d'une vidéo. */
const dateDe = (r: Row, onglet: Onglet) => onglet === 'live' ? r.startDate : (r.endDate ?? r.startDate);

// ── Carte et villes ──────────────────────────────────────────────────────────

/** Les villes de l'API, au format de points de la carte du tableau de bord. */
const lieux = (h: Histogram, tranche: number | null): Coordinate[] =>
  // Une barre survolée : seuls les spectateurs présents à ce moment-là. Sinon, toute la période.
  (tranche !== null ? h.buckets[tranche]?.locations ?? h.locations : h.locations).map(l => ({
    lat  : l.lat,
    lng  : l.lng,
    info1: [l.city, l.country].filter(Boolean).join(', '),
    info2: `${l.viewers} spectateur${l.viewers > 1 ? 's' : ''}${DISTINGUER_MODES && l.mode ? ` — ${l.mode === 'XTREAM_CODE' ? 'Xtream Code' : 'HLS'}` : ''}`,
    // Les couleurs de la carte du tableau de bord de la chaîne : rouge = HLS, bleu = Xtream Code.
    color: DISTINGUER_MODES && l.mode !== 'XTREAM_CODE' ? COULEUR_HLS : COULEUR_XTREAM,
  }));

const cleLieu = (l: { lat: number; lng: number; mode?: string }) => `${l.lat}|${l.lng}|${DISTINGUER_MODES ? l.mode ?? '' : ''}`;

/** Les villes classées : une ligne par ville (les deux modes réunis), avec l'indice du point de la carte. */
const villes = (h: Histogram, tranche: number | null) => {
  const source: ViewerLocation[] = tranche !== null ? h.buckets[tranche]?.locations ?? h.locations : h.locations;
  const parCle = new Map<string, { city: string; country: string; viewers: number; index: number }>();
  for (const l of source) {
    const k = cleLieu(l);
    const e = parCle.get(k);
    if (e) { e.viewers += l.viewers; continue; }
    parCle.set(k, { city: l.city || 'Lieu inconnu', country: l.country, viewers: l.viewers, index: h.locations.findIndex(x => cleLieu(x) === k) });
  }
  return Array.from(parCle.values()).sort((a, b) => b.viewers - a.viewers);
};

// ── Composant ────────────────────────────────────────────────────────────────

const StatisticsV2: React.FC = () => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const adminId = authentication.admin?.id ?? null;
  // IP et identités des spectateurs : le Superadmin seul (l'API refuse les autres).
  const estSuperadmin = authentication.admin?.role === 'ADMIN';

  const [ onglet   , setOnglet    ] = useState<Onglet>('live');
  const [ rows     , setRows      ] = useState<Row[]>([]);
  const [ loading  , setLoading   ] = useState(false);
  const [ erreur   , setErreur    ] = useState<string | null>(null);
  const [ openRow  , setOpenRow   ] = useState<Row | null>(null);
  const [ histo    , setHisto     ] = useState<Histogram | null>(null);
  const [ histoBusy, setHistoBusy ] = useState(false);
  const [ pas      , setPas       ] = useState<string | null>(null);
  const [ pasAuto  , setPasAuto   ] = useState(true);
  const [ majLe    , setMajLe     ] = useState<Date | null>(null);
  const [ majEnCours, setMajEnCours ] = useState(false);
  // Mémorisé dans le navigateur ; sans stockage (fenêtre privée, accès bloqué), on reste sur « coché ».
  const [ afficherCumuls, setAfficherCumulsEtat ] = useState<boolean>(() => {
    try { return window.localStorage.getItem(CLE_CUMULS) !== '0'; } catch { return true; }
  });
  const setAfficherCumuls = (v: boolean) => {
    setAfficherCumulsEtat(v);
    try { window.localStorage.setItem(CLE_CUMULS, v ? '1' : '0'); } catch { /* sans conséquence */ }
  };
  const [ zoomLimites, setZoomLimites ] = useState({ peutZoomer: false, peutDezoomer: false });
  const zoomApiRef = useRef<{ avant: () => void; arriere: () => void; tout: () => void } | null>(null);
  const rafraichirRef = useRef<(() => Promise<void>) | null>(null);
  const [ grapheAgrandi, setGrapheAgrandi ] = useState(false);
  const [ recherche , setRecherche  ] = useState('');
  const [ tri       , setTri        ] = useState<Tri>('date');
  const [ trancheSurvolee, setTrancheSurvolee ] = useState<number | null>(null);
  const [ villeSurvolee  , setVilleSurvolee   ] = useState<number | null>(null);
  // Détail d'une barre cliquée : la tranche, puis ceux qui y regardaient.
  const [ detailTranche, setDetailTranche ] = useState<{ debut: string; fin: string } | null>(null);
  const [ detailSpectateurs, setDetailSpectateurs ] = useState<ViewerDetail[] | null>(null);
  const [ detailErreur, setDetailErreur ] = useState(false);

  // Survol d'une tranche : les non localisés de cette tranche seulement (repli : toute la période).
  const nonLocalises = histo === null ? 0
    : trancheSurvolee !== null ? histo.buckets[trancheSurvolee]?.unlocated ?? histo.unlocated
    : histo.unlocated;

  // Une ville visée (carte ou liste) : les tranches où des spectateurs de cette ville étaient présents.
  const tranchesDeLaVille: number[] = (() => {
    if (histo === null || villeSurvolee === null) return [];
    const v = histo.locations[villeSurvolee];
    if (!v) return [];
    return histo.buckets.flatMap((b, i) => b.locations?.some(l => cleLieu(l) === cleLieu(v)) ? [i] : []);
  })();

  // Ce que le relevé périodique doit connaître À L'INSTANT où il tourne : le
  // minuteur est posé une fois par onglet, il ne verrait sinon que des valeurs périmées.
  const openRowRef  = useRef<Row | null>(null);
  const stepRef     = useRef<number | undefined>(undefined);   // pas choisi à la main ; absent = automatique
  openRowRef.current = openRow;

  useEffect(() => {
    if (adminId === null) return;

    let annule = false;
    setLoading(true);
    setErreur(null);
    setRows([]);
    setOpenRow(null);
    setHisto(null);
    setPas(null);
    stepRef.current = undefined;

    const charger = onglet === 'live'
      ? fetchLiveStats(adminId).then((r: LiveStatRow[]) => r as Row[])
      : fetchVideoStats(adminId).then((r: VideoStatRow[]) => r.map(v => ({ ...v, active: false })));

    charger
      .then(r => { if (!annule) { setRows(r); setMajLe(new Date()); } })
      .catch(() => { if (!annule) setErreur('Impossible de charger les statistiques'); })
      .finally(() => { if (!annule) setLoading(false); });

    return () => { annule = true; };
  }, [onglet, adminId]);

  // Relevé silencieux : ni « Chargement… », ni retour à la liste, ni perte du pas
  // choisi. Un échec ne casse rien — on garde ce qui est affiché jusqu'au prochain tour.
  useEffect(() => {
    if (adminId === null) return;

    const rafraichir = async () => {
      if (document.hidden) return;

      try {
        const liste: Row[] = onglet === 'live'
          ? await fetchLiveStats(adminId)
          : (await fetchVideoStats(adminId)).map(v => ({ ...v, active: false }));

        setRows(liste);

        const ouverte = openRowRef.current;

        if (ouverte !== null) {
          const h = onglet === 'live'
            ? await fetchLiveHistogram(adminId, ouverte.id, stepRef.current)
            : await fetchVideoHistogram(adminId, ouverte.id, stepRef.current);

          // L'utilisateur a pu changer de ligne pendant l'appel.
          if (openRowRef.current?.id === ouverte.id) {
            setHisto(h);
            if (stepRef.current === undefined) setPas(labelDuPas(h.bucketSeconds));
            // L'en-tête du détail suit la ligne (spectateurs, fin de diffusion).
            const fraiche = liste.find(r => r.id === ouverte.id);
            if (fraiche) setOpenRow(fraiche);
          }
        }

        setMajLe(new Date());
      } catch {
        /* silencieux : on réessaie à la prochaine minute */
      }
    };

    rafraichirRef.current = rafraichir;

    const timer = window.setInterval(rafraichir, RAFRAICHISSEMENT_MS);

    // Onglet resté en arrière-plan : on rattrape tout de suite au retour.
    const auRetour = () => { if (!document.hidden) rafraichir(); };
    document.addEventListener('visibilitychange', auRetour);

    return () => {
      rafraichirRef.current = null;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [onglet, adminId]);

  // Relevé à la demande : le même que celui de la minute, sans attendre le minuteur.
  const forcerRafraichissement = async () => {
    if (majEnCours || !rafraichirRef.current) return;
    setMajEnCours(true);
    try { await rafraichirRef.current(); } finally { setMajEnCours(false); }
  };

  const releve = (
    <>
      {majLe && <span className={styles.liveInd}>Relevé à {heure(majLe.toISOString())} · chaque minute</span>}
      <button type="button" className={styles.btn} onClick={forcerRafraichissement} disabled={majEnCours} title="Forcer le rafraîchissement">
        {majEnCours ? '…' : '↻'} Actualiser
      </button>
    </>
  );

  const chargerHisto = async (row: Row, step?: number) => {
    if (adminId === null) return;

    setHistoBusy(true);

    try {
      const h = onglet === 'live' ? await fetchLiveHistogram(adminId, row.id, step) : await fetchVideoHistogram(adminId, row.id, step);
      setHisto(h);
      // Le pas affiché est celui que l'API a retenu (relevé si trop de barres).
      setPas(labelDuPas(h.bucketSeconds));
    } catch {
      setErreur("Impossible de charger l'histogramme");
      setOpenRow(null);
    } finally {
      setHistoBusy(false);
    }
  };

  const ouvrirGraphe = (row: Row) => {
    stepRef.current = undefined;
    setPasAuto(true);
    setErreur(null);
    setOpenRow(row);
    setHisto(null);
    setPas(null);
    chargerHisto(row);
    window.scrollTo?.({ top: 0 });
  };

  const choisirPas = (label: string) => {
    const choisi = PAS.find(p => p.label === label);
    if (openRow && choisi) { stepRef.current = choisi.seconds; setPasAuto(false); setPas(choisi.label); chargerHisto(openRow, choisi.seconds); }
  };

  const pasAutomatique = () => {
    if (!openRow) return;
    stepRef.current = undefined;
    setPasAuto(true);
    chargerHisto(openRow);
  };

  const fermerGraphe = () => { setTrancheSurvolee(null); setVilleSurvolee(null); stepRef.current = undefined; setOpenRow(null); setHisto(null); setPas(null); };

  // ── Liste : filtre, tri, regroupement ──
  const lignes = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    const l = q === '' ? rows : rows.filter(r => r.name.toLowerCase().includes(q));
    return [...l].sort((a, b) => tri === 'audience'
      ? b.viewers - a.viewers
      : new Date(dateDe(b, onglet)).getTime() - new Date(dateDe(a, onglet)).getTime());
  }, [rows, recherche, tri, onglet]);

  const groupes = useMemo(() => {
    const out: { titre: string; items: Row[] }[] = [];
    for (const r of lignes) {
      const titre = jourDe(dateDe(r, onglet));
      const g = out.find(x => x.titre === titre) ?? (out.push({ titre, items: [] }), out[out.length - 1]);
      g.items.push(r);
    }
    return out;
  }, [lignes, onglet]);

  const audienceMax = Math.max(1, ...rows.map(r => r.viewers));
  const meilleure   = lignes.length > 2 ? Math.max(...lignes.map(r => r.viewers)) : -1;

  // ── Chiffres du haut de la liste ──
  const chiffres = useMemo(() => {
    const now   = Date.now();
    const debut = new Date(); debut.setHours(0, 0, 0, 0);
    const d0    = debut.getTime() - 6 * JOUR_MS;           // début des 7 jours
    const dAv   = d0 - 7 * JOUR_MS;                        // les 7 jours d'avant
    const ts    = (r: Row) => new Date(dateDe(r, onglet)).getTime();
    const semaine = rows.filter(r => ts(r) >= d0);
    const avant   = rows.filter(r => ts(r) >= dAv && ts(r) < d0);
    const jours   = Array.from({ length: 7 }, (_, i) => semaine.filter(r => ts(r) >= d0 + i * JOUR_MS && ts(r) < d0 + (i + 1) * JOUR_MS).length);
    const somme   = (l: Row[]) => l.reduce((s, r) => s + r.viewers, 0);
    const best    = rows.reduce<Row | null>((a, r) => !a || r.viewers > a.viewers ? r : a, null);
    return {
      actifs      : rows.filter(r => r.active),
      semaine     : semaine.length,
      jours,
      spectateurs : somme(semaine),
      evolution   : somme(avant) > 0 ? Math.round((somme(semaine) - somme(avant)) / somme(avant) * 100) : null,
      best,
      aujourdhui  : rows.filter(r => ts(r) >= debut.getTime()).length,
      enCeMoment  : rows.filter(r => r.endDate && now - new Date(r.endDate).getTime() < 10 * 60_000).length,
    };
  }, [rows, onglet]);
  const jourMax = Math.max(1, ...chiffres.jours);

  // ── Chiffres du détail ──
  const detail = useMemo(() => {
    if (!histo || histo.buckets.length === 0) return null;
    const derniere = histo.buckets[histo.buckets.length - 1];
    const picIndex = histo.buckets.reduce((best, b, i) => b.viewers > histo.buckets[best].viewers ? i : best, 0);
    const pic      = histo.buckets[picIndex];
    const uniques  = Math.max(derniere.cumulative ?? 0, openRow?.viewers ?? 0);
    return { derniere: derniere.viewers, pic: pic.viewers, picA: pic.start, part: uniques > 0 ? Math.round(pic.viewers / uniques * 100) : 0, uniques, villes: villes(histo, null).length };
  }, [histo, openRow]);

  /** Clic sur une barre : la fenêtre liste ceux qui regardaient pendant cette tranche. */
  const ouvrirDetailTranche = (i: number) => {
    if (!histo || adminId === null || !openRow) return;
    const b = histo.buckets[i];
    if (!b) return;
    const fin = histo.buckets[i + 1]?.start ?? new Date(new Date(b.start).getTime() + histo.bucketSeconds * 1000).toISOString();
    setDetailTranche({ debut: b.start, fin });
    setDetailSpectateurs(null);
    setDetailErreur(false);
    fetchViewersDetail(adminId, onglet, openRow.id, b.start, fin)
      .then(setDetailSpectateurs)
      .catch(() => setDetailErreur(true));
  };
  const fermerDetailTranche = () => { setDetailTranche(null); setDetailSpectateurs(null); };

  const villesAffichees = histo ? villes(histo, trancheSurvolee) : [];
  const villeMax = Math.max(1, ...villesAffichees.map(v => v.viewers));
  const cleSurvolee = histo && villeSurvolee !== null && histo.locations[villeSurvolee] ? cleLieu(histo.locations[villeSurvolee]) : null;

  const ouvrirAuClavier = (r: Row) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrirGraphe(r); }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // Détail d'une diffusion ou d'une vidéo
  // ════════════════════════════════════════════════════════════════════════════
  if (openRow !== null) {
    const enDirect = onglet === 'live' && openRow.active;
    const fin      = openRow.endDate ? new Date(openRow.endDate).getTime() : Date.now();

    return (
      <div className={styles.page}>
        <nav className={styles.crumbs} aria-label="Fil d'Ariane">
          <button type="button" onClick={fermerGraphe}>← Statistiques</button><span>/</span><span>{onglet === 'live' ? 'Lives' : 'Vidéos'}</span>
        </nav>

        <div className={styles.detailHead}>
          <h1>{openRow.name}</h1>
          {enDirect && <span className={`${styles.badge} ${styles.badgeLive}`}><span className={styles.liveDot}/>EN DIRECT</span>}
          <span className={styles.period}>
            {onglet === 'live'
              ? `${jourDe(openRow.startDate)}, ${heure(openRow.startDate)} → ${enDirect ? 'en cours' : finDe(openRow)}`
              : `Première lecture ${momentDe(openRow.startDate)} · dernière ${momentDe(openRow.endDate)}`}
          </span>
          <span className={styles.spacer}/>
          {releve}
        </div>

        <div className={styles.kpis}>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Spectateurs uniques</span>
            <span className={styles.kpiValue}>{detail ? nombre(detail.uniques) : nombre(openRow.viewers)}</span>
            <span className={styles.kpiSub}>adresses distinctes {onglet === 'live' ? 'depuis l’allumage' : 'depuis la première lecture'}</span>
          </div>
          <div className={`${styles.kpi} ${enDirect ? styles.kpiLive : ''}`}>
            <span className={styles.kpiLabel}>{enDirect ? 'Présents maintenant' : 'Présents à la fin'}</span>
            <span className={`${styles.kpiValue} ${enDirect ? styles.red : ''}`}>{detail ? nombre(detail.derniere) : '–'}</span>
            <span className={styles.kpiSub}>{pas ? `dans la dernière tranche de ${pas}` : ' '}</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Pic de présence</span>
            <span className={styles.kpiValue}>{detail ? <>{nombre(detail.pic)}<small>{momentDe(detail.picA)}</small></> : '–'}</span>
            <span className={styles.kpiSub}>{detail ? `${detail.part} % des spectateurs présents en même temps` : ' '}</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>{onglet === 'live' ? 'Durée de la diffusion' : 'Période de lecture'}</span>
            <span className={styles.kpiValue}>{duree(fin - new Date(openRow.startDate).getTime())}</span>
            <span className={styles.kpiSub}>{detail ? `${pluriel(detail.villes, 'ville')}${histo && histo.unlocated > 0 ? ` · ${nombre(histo.unlocated)} non localisé${histo.unlocated > 1 ? 's' : ''}` : ''}` : ' '}</span>
          </div>
        </div>

        {erreur && <p className={styles.error}>{erreur}</p>}

        {/* ── Le graphe ── */}
        <section className={styles.box}>
          <div className={styles.boxHead}>
            <h2>Fréquentation</h2>
            <span className={styles.legend}>
              {DISTINGUER_MODES
                ? <><span><i style={{ background: COULEUR_HLS }}/>HLS</span><span><i style={{ background: COULEUR_XTREAM }}/>Xtream Code</span></>
                : <span><i style={{ background: COULEUR_XTREAM }}/>Présents</span>}
              {afficherCumuls && <span><i className={styles.legendLine} style={{ background: COULEUR_CUMUL }}/>Cumul des spectateurs</span>}
            </span>
            <span className={styles.spacer}/>
            <span className={styles.toolLabel}>Pas</span>
            <div role="group" aria-label="Pas" className={styles.segment}>
              <button type="button" aria-pressed={pasAuto} className={pasAuto ? styles.segOn : undefined} onClick={pasAutomatique}>
                Auto{pasAuto && pas ? ` · ${pas}` : ''}
              </button>
            </div>
            <select className={styles.select} aria-label="Choisir le pas" value={pasAuto ? '' : (pas ?? '')} onChange={e => choisirPas(e.target.value)}>
              <option value="" disabled>Autre pas…</option>
              {PAS.map(p => <option key={p.label} value={p.label}>{p.label}</option>)}
            </select>
            <label className={styles.check}>
              <input type="checkbox" checked={afficherCumuls} onChange={e => setAfficherCumuls(e.target.checked)} /> Cumul
            </label>
            <div className={styles.zoom}>
              <button type="button" className={styles.btn} title="Dézoomer" aria-label="Dézoomer" disabled={!zoomLimites.peutDezoomer} onClick={() => zoomApiRef.current?.arriere()}>−</button>
              <button type="button" className={styles.btn} title="Zoomer"   aria-label="Zoomer"   disabled={!zoomLimites.peutZoomer}   onClick={() => zoomApiRef.current?.avant()}>+</button>
            </div>
          </div>

          {histoBusy && !histo && <span className={styles.hint}>Chargement…</span>}

          {histo && (
            histo.buckets.length === 0
              ? <span className={styles.hint}>Aucun spectateur sur cette période.</span>
              : <div className={styles.graph}>
                  <ExtendablePanel logoUrl="/images/logo.png" logoPosition="upperRight" buttonSide="left" onStateChange={setGrapheAgrandi}>
                    <CartesianGraph2
                      data = {histo.buckets.map(b => ({
                        group : formatTranche(b.start, histo.bucketSeconds),
                        series: DISTINGUER_MODES
                          ? [
                              { name: 'HLS',         value: b.viewersHls ?? b.viewers },
                              { name: 'Xtream Code', value: b.viewersXtream ?? 0 },
                            ]
                          : [{ name: 'Présents', value: b.viewers }],
                      }))}
                      secondaryCurve     = {afficherCumuls ? { name: 'Cumulé', values: histo.buckets.map(b => b.cumulative), color: COULEUR_CUMUL } : undefined}
                      yAxisLabel         = "Spectateurs présents"
                      colors             = {DISTINGUER_MODES ? [COULEUR_HLS, COULEUR_XTREAM] : [COULEUR_XTREAM]}
                      cumul              = {true}
                      tooltipLines       = {DISTINGUER_MODES ? (i => {
                        const b = histo.buckets[i];
                        return [`Simultanés : ${b.viewers}`, `HLS : ${b.viewersHls ?? b.viewers}`, `Xtream Code : ${b.viewersXtream ?? 0}`];
                      }) : undefined}
                      titleFontSize      = {24}
                      subTitleFontSize   = {21}
                      xAxisLabelFontSize = {15}
                      xAxisEndsOnly      = {true}
                      xAxisStartLabel    = {onglet === 'video' ? dateHeure(openRow.startDate) : undefined}
                      xAxisEndLabel      = {onglet === 'video' && openRow.endDate ? dateHeure(openRow.endDate) : undefined}
                      onHoverGroup       = {setTrancheSurvolee}
                      onClickGroup       = {estSuperadmin ? ouvrirDetailTranche : undefined}
                      highlightIndices   = {tranchesDeLaVille}
                      zoomable           = {true}
                      zoomApiRef         = {zoomApiRef}
                      onZoomLimits       = {setZoomLimites}
                      zoomResetKey       = {`${openRow.id}-${pas ?? ''}`}
                      yAxisLabelFontSize = {15}
                      legendFontSize     = {16}
                      // Réduit : le titre est dans l'en-tête de la boîte. Agrandi : il n'y a plus
                      // d'en-tête autour, le graphe se présente seul.
                      title              = {grapheAgrandi ? openRow.name : ''}
                      subTitle           = {grapheAgrandi ? `Par tranche de ${pas ?? ''}` : ''}
                      paddingTop         = {grapheAgrandi ? 135 : 75}
                      paddingBottom      = {45}
                      paddingLeft        = {100}
                      width              = {1200}
                      height             = {400}
                      grapheType         = 'Bar'
                      showLegend         = {false}
                    />
                  </ExtendablePanel>
                </div>
          )}
          <span className={styles.hint}>Survolez une barre : la carte et les villes ne montrent plus que les spectateurs présents à ce moment-là.{estSuperadmin && ' Cliquez dessus pour voir qui regardait.'}</span>
        </section>

        {/* ── Carte et villes ── */}
        {histo && (
          <div className={styles.twoCol}>
            <section className={styles.box}>
              <div className={styles.boxHead}>
                <h2>Origine des spectateurs</h2>
                <span className={styles.spacer}/>
                <span className={styles.hint}>{trancheSurvolee !== null && histo.buckets[trancheSurvolee]
                  ? `présents à ${heure(histo.buckets[trancheSurvolee].start)}`
                  : (onglet === 'live' ? 'toute la diffusion' : 'toute la période')}</span>
              </div>
              <div className={styles.map}>
                <ExtendablePanel logoUrl="/images/logo.png" logoPosition="bottomLeft">
                  <OpenStreetMapV2
                    coordinates  = {lieux(histo, trancheSurvolee)}
                    initialZoom  = {2}
                    renderMode   = "dark"
                    mapColors    = {{
                      water     : '#1b2947',
                      land      : '#263d6e',
                      landcover : '#263d6e',
                      road      : '#2a2a4a',
                      building  : '#222244',
                      labelText : '#c8d6e5',
                      labelHalo : '#0d1b2a',
                    }}
                    cityLabelsOnly
                    autoCenter   = {true}
                    fitPoints    = {true}
                    emptyCenter  = {histo.viewer ?? null}
                    onPointHover = {setVilleSurvolee}
                  />
                </ExtendablePanel>
                {nonLocalises > 0 &&
                  <span className={styles.mapNote}>{nombre(nonLocalises)} spectateur{nonLocalises > 1 ? 's' : ''} non localisé{nonLocalises > 1 ? 's' : ''}</span>}
              </div>
            </section>

            <section className={styles.box}>
              <div className={styles.boxHead}>
                <h2>Villes</h2>
                <span className={styles.spacer}/>
                <span className={styles.hint}>{pluriel(villesAffichees.length, 'ville')}</span>
              </div>
              {villesAffichees.length === 0
                ? <span className={styles.hint}>Aucune ville connue{trancheSurvolee !== null ? ' à ce moment-là' : ''}.</span>
                : <div className={styles.cities}>
                    {villesAffichees.map(v => {
                      const cle = v.index >= 0 ? cleLieu(histo.locations[v.index]) : null;
                      return (
                        <div key={`${v.city}-${v.index}`} className={`${styles.city} ${cle && cle === cleSurvolee ? styles.cityOn : ''}`}
                             onMouseEnter={() => setVilleSurvolee(v.index >= 0 ? v.index : null)}
                             onMouseLeave={() => setVilleSurvolee(null)}>
                          <span title={[v.city, v.country].filter(Boolean).join(', ')}>{v.city} {v.country && <small>{v.country}</small>}</span>
                          <span className={styles.track}><i style={{ width: `${v.viewers / villeMax * 100}%` }}/></span>
                          <span>{nombre(v.viewers)}</span>
                        </div>
                      );
                    })}
                  </div>}
              <span className={styles.hint}>Survolez une ville : ses tranches s'éclairent dans le graphe.</span>
            </section>
          </div>
        )}

        {detailTranche && (
          <div className={styles.modalVoile} onClick={fermerDetailTranche}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Détail des spectateurs" onClick={e => e.stopPropagation()}>
              <header>
                <h3>Spectateurs de {dateHeure(detailTranche.debut)} à {heure(detailTranche.fin)}</h3>
                <button type="button" className={styles.btn} onClick={fermerDetailTranche} aria-label="Fermer">✕</button>
              </header>
              {detailErreur ? <span className={styles.hint}>Impossible de charger le détail.</span>
                : detailSpectateurs === null ? <span className={styles.hint}>Chargement…</span>
                : detailSpectateurs.length === 0 ? <span className={styles.hint}>Personne sur cette tranche.</span>
                : <div className={styles.modalTable}>
                    <table>
                      <thead><tr><th>IP</th><th>Localisation</th><th>Nom</th><th>Email</th></tr></thead>
                      <tbody>
                        {detailSpectateurs.map((v, i) => {
                          const nom = [v.firstname, v.lastname].filter(Boolean).join(' ');
                          return (
                            <tr key={`${v.ip}-${v.email ?? ''}-${i}`}>
                              <td className={styles.num}>{v.ip}</td>
                              <td>{v.countryCode && <span className={`fi fi-${v.countryCode.toLowerCase()}`} title={v.country ?? ''}/>} {v.city || (v.country ? v.country : <span className={styles.muted}>Non localisé</span>)}</td>
                              <td>{nom || <span className={styles.muted}>—</span>}</td>
                              <td>{v.email || <span className={styles.muted}>Anonyme</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>}
              <span className={styles.hint}>{detailSpectateurs ? `${nombre(detailSpectateurs.length)} spectateur${detailSpectateurs.length > 1 ? 's' : ''}` : ' '}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // Liste des diffusions ou des vidéos
  // ════════════════════════════════════════════════════════════════════════════
  const ligne = (r: Row) => {
    const enDirect = onglet === 'live' && r.active;
    const debut    = new Date(r.startDate).getTime();
    const fin      = r.endDate ? new Date(r.endDate).getTime() : Date.now();
    return (
      <div key={r.id} className={`${styles.row} ${enDirect ? styles.rowLive : ''}`} role="button" tabIndex={0}
           aria-label={`Ouvrir le détail de ${r.name}`} onClick={() => ouvrirGraphe(r)} onKeyDown={ouvrirAuClavier(r)}>
        <span className={styles.tile} style={{ background: teinte(r.name) }} aria-hidden="true">{initiales(r.name)}</span>
        <div className={styles.name}>
          <strong title={r.name}>{r.name}</strong>
          <span>
            {enDirect && <span className={`${styles.badge} ${styles.badgeLive}`}><span className={styles.liveDot}/>EN DIRECT</span>}
            {r.viewers === meilleure && <span className={`${styles.badge} ${styles.badgeBest}`}>MEILLEURE AUDIENCE</span>}
            {!enDirect && r.viewers !== meilleure && (onglet === 'live' ? 'Diffusion terminée' : 'Vidéo')}
          </span>
        </div>
        {onglet === 'live'
          ? <div className={styles.when}>
              <span>{heure(r.startDate)} → {enDirect ? <span className={styles.red}>en cours</span> : finDe(r)}</span>
              <small>{duree(fin - debut)}{enDirect ? ' pour l’instant' : ''}</small>
            </div>
          : <div className={styles.when}>
              <span>Dernière lecture {momentDe(r.endDate)}</span>
              <small>Première : {momentDe(r.startDate)}</small>
            </div>}
        <div className={styles.gaugeCell}>
          <div className={styles.gaugeTop}><span>spectateurs</span><strong>{nombre(r.viewers)}</strong></div>
          <div className={styles.gauge}><i style={{ width: `${Math.max(2, r.viewers / audienceMax * 100)}%` }}/></div>
        </div>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary} ${styles.open}`} tabIndex={-1}>Voir le détail →</button>
      </div>
    );
  };

  const best = chiffres.best;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1>Statistiques</h1>
          <p>Fréquentation de vos lives et de vos vidéos.</p>
        </div>
        <span className={styles.spacer}/>
        <div role="group" aria-label="Lives ou vidéos" className={styles.segment}>
          <button type="button" aria-pressed={onglet === 'live'}  className={onglet === 'live'  ? styles.segOn : undefined} onClick={() => setOnglet('live')}>Lives</button>
          <button type="button" aria-pressed={onglet === 'video'} className={onglet === 'video' ? styles.segOn : undefined} onClick={() => setOnglet('video')}>Vidéos</button>
        </div>
      </div>

      {/* ── Chiffres ── */}
      <div className={styles.kpis}>
        {onglet === 'live' ? <>
          <div className={`${styles.kpi} ${chiffres.actifs.length > 0 ? styles.kpiLive : ''}`}>
            <span className={styles.kpiLabel}>En direct maintenant</span>
            <span className={`${styles.kpiValue} ${chiffres.actifs.length > 0 ? styles.red : ''}`}>{chiffres.actifs.length}</span>
            <span className={styles.kpiSub}>{chiffres.actifs.length > 0 ? chiffres.actifs.map(r => r.name).join(', ') : 'aucune chaîne allumée'}</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Diffusions, 7 jours</span>
            <span className={styles.kpiValue}>{chiffres.semaine}</span>
            <div className={styles.spark} aria-hidden="true">
              {chiffres.jours.map((n, i) => <span key={i} style={{ height: `${Math.max(3, Math.round(n / jourMax * 22))}px` }}/>)}
            </div>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Spectateurs, 7 jours</span>
            <span className={styles.kpiValue}>{nombre(chiffres.spectateurs)}</span>
            <span className={chiffres.evolution !== null && chiffres.evolution >= 0 ? `${styles.kpiSub} ${styles.green}` : styles.kpiSub}>
              {chiffres.evolution === null ? 'additionnés sur les diffusions' : `${chiffres.evolution > 0 ? '+' : ''}${chiffres.evolution} % par rapport aux 7 jours d’avant`}
            </span>
          </div>
        </> : <>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Vidéos regardées aujourd'hui</span>
            <span className={styles.kpiValue}>{chiffres.aujourdhui}</span>
            <span className={styles.kpiSub}>sur {pluriel(rows.length, 'vidéo')} qui ont eu des spectateurs</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Regardées sur 7 jours</span>
            <span className={styles.kpiValue}>{chiffres.semaine}</span>
            <div className={styles.spark} aria-hidden="true">
              {chiffres.jours.map((n, i) => <span key={i} style={{ height: `${Math.max(3, Math.round(n / jourMax * 22))}px` }}/>)}
            </div>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Regardées en ce moment</span>
            <span className={styles.kpiValue}>{chiffres.enCeMoment}</span>
            <span className={styles.kpiSub}>dernière lecture il y a moins de 10 min</span>
          </div>
        </>}
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>{onglet === 'live' ? 'Meilleure audience' : 'La plus regardée'}</span>
          <span className={styles.kpiValue}>{best ? <>{nombre(best.viewers)}<small>spectateurs</small></> : '–'}</span>
          <span className={styles.kpiSub}>{best ? (onglet === 'live' ? `${best.name}, ${jourDe(best.startDate).toLowerCase()} ${heure(best.startDate)}` : best.name) : ' '}</span>
        </div>
      </div>

      {/* ── Recherche et tri ── */}
      <div className={styles.toolbar}>
        <label className={styles.search}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
          <input type="search" value={recherche} onChange={e => setRecherche(e.target.value)}
                 placeholder={onglet === 'live' ? 'Rechercher une chaîne' : 'Rechercher une vidéo'} aria-label="Rechercher" />
        </label>
        <span className={styles.toolLabel}>Trier</span>
        <div role="group" aria-label="Tri" className={styles.segment}>
          <button type="button" aria-pressed={tri === 'date'}     className={tri === 'date'     ? styles.segOn : undefined} onClick={() => setTri('date')}>Les plus récentes</button>
          <button type="button" aria-pressed={tri === 'audience'} className={tri === 'audience' ? styles.segOn : undefined} onClick={() => setTri('audience')}>Meilleure audience</button>
        </div>
        <span className={styles.spacer}/>
        {releve}
      </div>

      {loading && <div className={styles.empty}><strong>Chargement…</strong></div>}
      {erreur  && <div className={styles.empty}><strong className={styles.error}>{erreur}</strong><span>Le prochain relevé, dans une minute, réessaiera.</span></div>}

      {!loading && !erreur && lignes.length === 0 && (
        <div className={styles.empty}>
          <strong>{rows.length > 0 ? 'Aucun résultat pour cette recherche' : onglet === 'live' ? 'Aucune diffusion enregistrée pour l’instant' : 'Aucune vidéo regardée pour l’instant'}</strong>
          <span>{rows.length > 0 ? 'Essayez un autre nom.' : onglet === 'live' ? "L'historique commence à la première chaîne allumée." : 'Une vidéo apparaît ici dès sa première lecture.'}</span>
        </div>
      )}

      {lignes.length > 0 && (tri === 'audience'
        ? <div className={styles.rows}>{lignes.map(ligne)}</div>
        : <div className={styles.days}>
            {groupes.map(g => (
              <section key={g.titre} className={styles.day}>
                <h2 className={styles.dayTitle}>{g.titre}
                  <span>{pluriel(g.items.length, onglet === 'live' ? 'diffusion' : 'vidéo')} · {pluriel(g.items.reduce((s, r) => s + r.viewers, 0), 'spectateur')}</span>
                </h2>
                <div className={styles.rows}>{g.items.map(ligne)}</div>
              </section>
            ))}
          </div>)}
    </div>
  );
};

export default StatisticsV2;
